import { Link } from 'react-router-dom';
import { Phone, Mail } from 'lucide-react';
import { siteConfig } from '../utils/seo';

interface FooterProps {
  onOpenCookieSettings: () => void;
}

const footerLinks = {
  services: [
    { name: 'Night-Auditor-Service', path: '/leistungen#night-audit' },
    { name: 'Receptionsservice', path: '/leistungen#rezeption' },
    { name: 'Event-Service', path: '/leistungen#events' },
  ],
  company: [
    { name: 'Über uns', path: '/ueber-uns' },
    { name: 'Karriere', path: '/jobs' },
    { name: 'Kontakt', path: '/kontakt' },
  ],
  legal: [
    { name: 'Impressum', path: '/impressum' },
    { name: 'Datenschutz', path: '/datenschutz' },
    { name: 'Cookie-Einstellungen', path: '#' },
  ],
};

export function Footer({ onOpenCookieSettings }: FooterProps) {
  const currentYear = new Date().getFullYear();

  const handleCookieClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onOpenCookieSettings();
  };

  return (
    <footer className="bg-brand-950 text-white" role="contentinfo">
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-platin-200 rounded-lg flex items-center justify-center">
                <span className="text-brand-950 font-bold text-xl">CU</span>
              </div>
              <span className="font-bold text-xl">ConciergeUnit</span>
            </div>
            <p className="text-platin-300 text-sm leading-relaxed">
              Professionelle Empfangsdienstleistungen für Hotellerie und Events. 
              Ihr zuverlässiger Partner für Night-Audit, Reception und Event-Support.
            </p>
            <div className="space-y-3">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-platin-300 hover:text-platin-100 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>{siteConfig.phone}</span>
              </a>
              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-center gap-3 text-platin-300 hover:text-platin-100 transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>{siteConfig.email}</span>
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="font-semibold text-lg mb-6 text-platin-200">Leistungen</h3>
            <ul className="space-y-3" role="list">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-platin-300 hover:text-platin-100 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-semibold text-lg mb-6 text-platin-200">Unternehmen</h3>
            <ul className="space-y-3" role="list">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-platin-300 hover:text-platin-100 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Column */}
          <div>
            <h3 className="font-semibold text-lg mb-6 text-platin-200">Rechtliches</h3>
            <ul className="space-y-3" role="list">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  {link.path === '#' ? (
                    <button
                      onClick={handleCookieClick}
                      className="text-platin-300 hover:text-platin-100 transition-colors text-left"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-platin-300 hover:text-platin-100 transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-platin-400 text-sm text-center md:text-left">
              © {currentYear} ConciergeUnit - {siteConfig.owner}. Alle Rechte vorbehalten.
            </p>
            <p className="text-platin-500 text-xs">
              Excellence in Hospitality
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
